import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Upload, Check, Play, Pause, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Placeholder voices
const presetVoices = [
  { id: '1', name: 'Sarah', description: 'Warm, professional female voice', accent: 'American', gender: 'Female' },
  { id: '2', name: 'Michael', description: 'Deep, authoritative male voice', accent: 'American', gender: 'Male' },
  { id: '3', name: 'Emma', description: 'Friendly, youthful female voice', accent: 'British', gender: 'Female' },
  { id: '4', name: 'James', description: 'Sophisticated British male voice', accent: 'British', gender: 'Male' },
  { id: '5', name: 'Sofia', description: 'Energetic, expressive female voice', accent: 'American', gender: 'Female' },
  { id: '6', name: 'David', description: 'Calm, reassuring male voice', accent: 'American', gender: 'Male' },
  { id: '7', name: 'Olivia', description: 'Clear, articulate female voice', accent: 'Australian', gender: 'Female' },
  { id: '8', name: 'William', description: 'Confident, engaging male voice', accent: 'British', gender: 'Male' },
  { id: '9', name: 'Ava', description: 'Soft, soothing female voice', accent: 'American', gender: 'Female' },
  { id: '10', name: 'Henry', description: 'Warm, storytelling male voice', accent: 'British', gender: 'Male' },
  { id: '11', name: 'Mia', description: 'Bright, cheerful female voice', accent: 'American', gender: 'Female' },
  { id: '12', name: 'Lucas', description: 'Casual, relatable male voice', accent: 'American', gender: 'Male' },
  { id: '13', name: 'Charlotte', description: 'Elegant, refined female voice', accent: 'British', gender: 'Female' },
  { id: '14', name: 'Ethan', description: 'Dynamic, versatile male voice', accent: 'American', gender: 'Male' },
  { id: '15', name: 'Amelia', description: 'Gentle, nurturing female voice', accent: 'Australian', gender: 'Female' },
  { id: '16', name: 'Noah', description: 'Friendly, approachable male voice', accent: 'American', gender: 'Male' },
  { id: '17', name: 'Isabella', description: 'Passionate, emotive female voice', accent: 'American', gender: 'Female' },
  { id: '18', name: 'Mason', description: 'Strong, motivational male voice', accent: 'American', gender: 'Male' },
  { id: '19', name: 'Harper', description: 'Modern, trendy female voice', accent: 'American', gender: 'Female' },
  { id: '20', name: 'Alexander', description: 'Classic, distinguished male voice', accent: 'British', gender: 'Male' },
]

export default function VoicesPage() {
  const [selectedVoice, setSelectedVoice] = useState<string | null>(null)
  const [playingVoice, setPlayingVoice] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleUpload = () => {
    setIsUploading(true)
    setTimeout(() => setIsUploading(false), 1500)
  }

  const handlePlayPreview = (voiceId: string) => {
    if (playingVoice === voiceId) {
      setPlayingVoice(null)
    } else {
      setPlayingVoice(voiceId)
      // Simulate audio playing for 2 seconds
      setTimeout(() => setPlayingVoice(null), 2000)
    }
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>Voices - Footage</title>
        <meta name="description" content="Manage your AI voices." />
      </Helmet>
      <div className="max-w-4xl">
        <h1 className="text-2xl font-bold text-neutral-900">Voices</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Choose a voice for your video narration or upload your own.
        </p>

        {/* Upload Section */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-neutral-700 mb-3">Upload Custom Voice</h2>
          <div className="border-2 border-dashed border-neutral-200 rounded-xl p-8 text-center hover:border-neutral-300 transition-colors">
            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
              <Upload className="h-5 w-5 text-neutral-400" />
            </div>
            <p className="text-sm text-neutral-600 mb-1">Upload a voice sample</p>
            <p className="text-xs text-neutral-400 mb-4">MP3, WAV up to 25MB (min 30 seconds)</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Browse Files'
              )}
            </Button>
          </div>
        </div>

        {/* Preset Voices */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold text-neutral-700 mb-3">Preset Voices</h2>
          <div className="grid gap-2">
            {presetVoices.map((voice) => (
              <button
                key={voice.id}
                onClick={() => setSelectedVoice(voice.id)}
                className={cn(
                  'flex items-center gap-4 p-3 rounded-xl border-2 transition-all text-left',
                  selectedVoice === voice.id
                    ? 'border-teal-500 bg-teal-50/50'
                    : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                )}
              >
                {/* Play button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePlayPreview(voice.id)
                  }}
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors',
                    playingVoice === voice.id
                      ? 'bg-teal-500 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  )}
                >
                  {playingVoice === voice.id ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4 ml-0.5" />
                  )}
                </button>

                {/* Voice info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-neutral-900">{voice.name}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                      {voice.gender}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                      {voice.accent}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">{voice.description}</p>
                </div>

                {/* Selected indicator */}
                {selectedVoice === voice.id && (
                  <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center shrink-0">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Voice Action */}
        {selectedVoice && (
          <div className="mt-6 flex items-center gap-3">
            <Button className="bg-teal-600 hover:bg-teal-700">
              Use Selected Voice
            </Button>
            <Button variant="ghost" onClick={() => setSelectedVoice(null)}>
              Clear Selection
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
